import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Lock01, Mail01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { HintText } from '@/components/base/input/hint-text';
import { Input, InputBase, TextField } from '@/components/base/input/input';
import { Label } from '@/components/base/input/label';
import { useAuthStore } from '@/shared/stores/authStore';
import { ApiError } from '@/shared/api/types';
import { AUTH_FORM_SIZE } from '@/shared/config/auth-form';
import { ROUTES } from '@/shared/config/constants';
import { KnowMatchLogo, ParticleSphere, TextLink } from '@/shared/ui';

const loginSchema = z.object({
  email: z.string().min(1, 'Informe seu email').email('Email inválido'),
  senha: z.string().min(6, 'Mínimo 6 caracteres'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export const LoginPage = () => {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [submitting, setSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', senha: '' },
    mode: 'onBlur',
  });

  // Habilita o botao Entrar apenas quando os dois campos tem conteudo nao-vazio.
  const watched = useWatch({ control, name: ['email', 'senha'] });
  const canSubmit = (watched[0]?.trim().length ?? 0) > 0 && (watched[1]?.length ?? 0) > 0;

  const onSubmit = async (values: LoginFormValues) => {
    setSubmitting(true);
    try {
      await login(values.email, values.senha);
      const user = useAuthStore.getState().user;
      toast.success('Bem-vindo de volta!');
      navigate(user?.onboardingCompleto ? ROUTES.APP.MATCHES : ROUTES.ONBOARDING, {
        replace: true,
      });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Falha ao entrar. Tente novamente.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-dvh w-full flex-col bg-white lg:grid lg:grid-cols-2">
      {/* Mobile only: logo no topo da viewport, estilo header de app. */}
      <header className="flex items-center justify-center px-6 pt-[calc(env(safe-area-inset-top)+24px)] pb-2 lg:hidden">
        <KnowMatchLogo className="h-16 w-auto text-gray-900" aria-label="Know-How Match" />
      </header>

      <aside className="relative hidden min-h-dvh flex-col overflow-hidden bg-black text-white lg:flex">
        <div className="relative z-10 flex items-center gap-3 p-10">
          <KnowMatchLogo className="h-9 w-auto text-white" />
        </div>

        <div className="absolute inset-0 flex items-center justify-center">
          <ParticleSphere className="aspect-square h-full max-h-[120%] w-full max-w-[120%]" />
        </div>

        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black via-black/70 to-transparent"
          aria-hidden="true"
        />

        <div className="relative z-10 mt-auto flex flex-col gap-3 p-10">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight">
            Networking as a service
          </h2>
          <p className="max-w-md text-base leading-relaxed text-white/60">
            Conectando conhecimento e oportunidades de forma inteligente e segura.
          </p>
        </div>
      </aside>

      <section className="flex flex-1 items-center justify-center px-6 py-6 sm:px-10 lg:min-h-dvh lg:py-16">
        <div className="w-full max-w-md">
          <header className="flex flex-col gap-2">
            <h1 className="text-3xl font-semibold tracking-tight text-gray-900 md:text-4xl">
              Bem-vindo de volta
            </h1>
            <p className="text-base text-gray-500">Entre com suas credenciais para continuar.</p>
          </header>

          <form className="mt-10 flex flex-col gap-5" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <Input
                  label="Email"
                  type="email"
                  size={AUTH_FORM_SIZE}
                  icon={Mail01}
                  placeholder="Digite seu e-mail"
                  autoComplete="email"
                  isInvalid={!!errors.email}
                  hint={errors.email?.message}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />

            <Controller
              control={control}
              name="senha"
              render={({ field }) => (
                <TextField
                  size={AUTH_FORM_SIZE}
                  isInvalid={!!errors.senha}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                >
                  <div className="flex w-full items-center justify-between gap-3">
                    <Label>Senha</Label>
                    <TextLink to={ROUTES.FORGOT_PASSWORD}>Esqueceu a senha?</TextLink>
                  </div>
                  <InputBase
                    type="password"
                    icon={Lock01}
                    placeholder="Digite sua senha"
                    autoComplete="current-password"
                  />
                  {errors.senha?.message && <HintText isInvalid>{errors.senha.message}</HintText>}
                </TextField>
              )}
            />

            <Button
              type="submit"
              size={AUTH_FORM_SIZE}
              color="primary"
              isLoading={submitting}
              isDisabled={!canSubmit}
              className="w-full bg-black ring-black hover:bg-gray-900 data-loading:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Entrar
            </Button>
          </form>

          <p className="mt-8 text-center text-sm text-gray-500">
            Não tem uma conta?{' '}
            <TextLink to={ROUTES.REGISTER} className="text-gray-900 hover:text-black">
              Criar conta
            </TextLink>
          </p>
        </div>
      </section>
    </main>
  );
};
